/**
 * useProcessingFeeGate — the single source of truth for the card processing-fee
 * gate inputs on every web2 checkout quote surface.
 *
 * The surcharge follows the **charging club**: an authenticated member's charge
 * routes through their HOME club (`PaymentService.forUser`), a guest's through
 * the visited club. Both the toggle (`passProcessingFees`) and the `provider`
 * must come from that one club so the quote matches the server charge — sourcing
 * them from different clubs (e.g. visited-club toggle + home-club provider) lets
 * a cross-club member's quote diverge from what they're actually charged.
 *
 * Feed the result straight into `resolveQuoteProcessingFee`. (Mobile once had
 * a `useClubProcessingFeeGate` twin; it was retired when mobile's confirm page
 * moved to the server purchase quote, which sizes the fee itself.)
 *
 * @module club/hooks
 */

import type { QuoteFeeProvider } from '@butter/library/pricing';
import { useAuth } from '~/services/auth';
import { detectPaymentProvider, getPaymentSettings } from '~/lib/utils/paymentProvider';
import { useClubQuery } from './useClubQuery';
import { useHomeClub } from './useHomeClub';

/**
 * Resolve `{ passProcessingFees, provider }` from the charging club
 * (`authenticated ? homeClub : visitedClub`).
 */
export function useProcessingFeeGate(): { passProcessingFees: boolean; provider: QuoteFeeProvider } {
  const { data: club } = useClubQuery();
  const { homeClub } = useHomeClub();
  const { isAuthenticated } = useAuth();

  const chargingClub = isAuthenticated ? homeClub : club;

  return {
    passProcessingFees: chargingClub?.passProcessingFees ?? false,
    provider: detectPaymentProvider(getPaymentSettings(chargingClub)),
  };
}
